import { useState, useCallback, useEffect, useRef, Suspense } from 'react';
import type { AnnotationTextSize, AnnotationTool } from '@annoteer/annotation-engine';
import type { DocumentInfo } from '../types/document.js';
import { useSync } from '../contexts/SyncContext.js';
import { useMeetingState } from '../contexts/LiveShareContext.js';
import { useUndoState } from '../hooks/useUndoState.js';
import { usePresence } from '../hooks/usePresence.js';
import { useFollowDocument } from '../hooks/useFollowDocument.js';
import { useFitZoom } from '../hooks/useFitZoom.js';
import { useDocFromUrl } from '../hooks/useDocFromUrl.js';
import { useBillingRefreshOnDocument } from '../hooks/useBillingRefreshOnDocument.js';
import { useAnnotatedExport } from '../hooks/useAnnotatedExport.js';
import { useAnnotatedImageExport } from '../hooks/useAnnotatedImageExport.js';
import type { ImageExportSources } from '../hooks/useAnnotatedImageExport.js';
import {
  resolveFollowPage,
  resolvePresenterView,
  shouldReturnToWhiteboard,
} from '../utils/presenter-view.js';
import { PdfViewer, ImageViewer, DocxEditor, SpreadsheetEditor } from './lazy-viewers.js';
import { AnnotationToolbar } from './AnnotationToolbar.js';
import { DocumentUpload } from './DocumentUpload.js';
import { ExtractionPanel } from './ExtractionPanel.js';
import { MeetingControls } from './MeetingControls.js';
import { UserAvatarStack } from './UserAvatarStack.js';
import { ConnectionStatus } from './ConnectionStatus.js';
import { ZoomControls } from './ZoomControls.js';
import { BrandMark } from './BrandMark.js';
import { DebugPanel } from './DebugPanel.js';
import { PickerInterruptedBanner } from './PickerInterruptedBanner.js';
import { BUILD_TAG } from '../build-tag.js';
import { formatConnectionRow } from '../utils/connection-row.js';
import { FILE_INPUT_ACCEPT, IS_COARSE_POINTER } from '../types/document.js';
import { useAuthFetch } from '../hooks/useAuthFetch.js';
import { WHITEBOARD_DOCUMENT_ID, isWhiteboard, whiteboardDocument } from '../utils/whiteboard.js';

/**
 * Keep `?docId=` in step with what is actually open.
 *
 * This is the whole reason the phone kept losing documents. The stage reads its
 * document from the URL -- share-to-stage puts the id there -- but it never
 * wrote to it, so after the user opened something of their own the URL still
 * named the PRESENTER's document. React state said one thing, the URL said
 * another, and only one of the two survives a remount.
 *
 * And the stage remounts readily on Teams mobile: the Android file picker
 * backgrounds the WebView, and Android reclaims and reloads it under memory
 * pressure. So every state-based guard (followOverride, the applied-id ref) was
 * wiped exactly when it was needed, the URL was re-read, and the presenter's
 * document came back. Two fixes on 2026-08-30 failed for this reason before the
 * asymmetry with SidePanel -- which has always stamped the URL -- was spotted.
 */
function stampDocIdInUrl(documentId: string) {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('docId', documentId);
    window.history.replaceState({}, '', url.toString());
  } catch {
    // A URL we cannot rewrite is not a reason to fail the upload.
  }
}

function clearDocIdInUrl() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('docId');
    window.history.replaceState({}, '', url.toString());
  } catch {
    // ignore
  }
}

/**
 * localStorage, NOT sessionStorage.
 *
 * sessionStorage is wiped when the WebView context itself is destroyed, which
 * is precisely the event these counters exist to detect -- so they reset in
 * lockstep with the thing they were measuring and reported "mounts: 1,
 * lastUpload: none" through every occurrence. A perfectly self-concealing
 * instrument, and it cost two rounds on 2026-08-31.
 *
 * localStorage survives that, so the counter only ever climbs. It is not reset
 * per meeting and is not meant to be: what matters is whether it moves across
 * one file-picker interaction.
 */
/**
 * Counts mounts across a WebView reload, which plain state cannot do. If this
 * climbs while the user only picked a file, the stage is being torn down and
 * rebuilt -- the thing worth knowing before theorising about follow mode again.
 */
function renderScale(): string {
  try {
    return window.localStorage.getItem('annoteer.renderScale') ?? 'none';
  } catch {
    return 'n/a';
  }
}

function lastExport(): string {
  try {
    return window.localStorage.getItem('annoteer.lastExport') ?? 'none';
  } catch {
    return 'n/a';
  }
}

function lastUpload(): string {
  try {
    return window.localStorage.getItem('annoteer.lastUpload') ?? 'none';
  } catch {
    return 'n/a';
  }
}

function countMount(): number {
  try {
    const n = Number(window.localStorage.getItem('annoteer.stageMounts') ?? '0') + 1;
    window.localStorage.setItem('annoteer.stageMounts', String(n));
    return n;
  } catch {
    return -1;
  }
}

export function MeetingStage() {
  const { connectionState, undoManager } = useSync();
  const { status: authStatus } = useAuthFetch();
  const { canUndo, canRedo } = useUndoState(undoManager);
  const { viewers } = usePresence();
  const { isFollowing, isSuspended, followModeState, endSuspension, eventCount } =
    useMeetingState();
  const [activeTool, setActiveTool] = useState<AnnotationTool>('select');
  const [activeColor, setActiveColor] = useState('#dc2626');
  const [activeStrokeWidth, setActiveStrokeWidth] = useState(2);
  const [activeTextSize, setActiveTextSize] = useState<AnnotationTextSize>('medium');
  const [documentInfo, setDocumentInfo] = useState<DocumentInfo | null>(null);
  useBillingRefreshOnDocument(documentInfo?.documentId);
  // With nothing open the stage shows the meeting's whiteboard rather than a
  // message. Only ever RENDERED, never stored in documentInfo -- see
  // whiteboard.ts for why (certification issue #14).
  //
  // A desktop can put the whiteboard away (it can open a document instead); a
  // phone cannot, so it keeps no close control. Closing is a view choice for
  // this stage and lasts the session -- it deletes nothing.
  const [whiteboardClosed, setWhiteboardClosed] = useState(false);
  const canCloseWhiteboard = !IS_COARSE_POINTER;
  const shownDocument =
    documentInfo ?? (whiteboardClosed && canCloseWhiteboard ? null : whiteboardDocument());
  const [showExtraction, setShowExtraction] = useState(false);
  // Tracked only so the state readout can show the page this stage is actually
  // on beside the page it is being told to show. Without both numbers a report
  // of "the page flickers" cannot distinguish an oscillating instruction from a
  // viewer that keeps resetting itself.
  const [currentPage, setCurrentPage] = useState(1);
  const {
    zoom: zoomLevel,
    fitZoom,
    fitMode,
    reportFitZoom,
    chooseZoom,
    chooseFit,
    resetZoom,
  } = useFitZoom();
  const {
    canExport: canExportAnnotated,
    isExporting,
    exportError,
    exportedName,
    exportAnnotated,
  } = useAnnotatedExport(documentInfo);
  // Images have no annotated export of their own -- ImageViewer hands us a
  // getter for the <img> and the Fabric layer, and we composite them here so
  // the Download button sits in the same place it does for every other format.
  const imageExportRef = useRef<(() => ImageExportSources | null) | null>(null);
  const [canExportImage, setCanExportImage] = useState(false);
  const handleImageExportReady = useCallback(
    (getSources: (() => ImageExportSources | null) | null) => {
      imageExportRef.current = getSources;
      setCanExportImage(getSources != null);
    },
    [],
  );
  const {
    isExporting: isExportingImage,
    exportError: imageExportError,
    exportedName: imageExportedName,
    exportAnnotatedImage,
  } = useAnnotatedImageExport(shownDocument?.fileName);
  const handleExportImage = useCallback(() => {
    const sources = imageExportRef.current?.();
    if (sources) void exportAnnotatedImage(sources);
  }, [exportAnnotatedImage]);
  const isFollowingRef = useRef(isFollowing);
  isFollowingRef.current = isFollowing;
  const isSuspendedRef = useRef(isSuspended);
  isSuspendedRef.current = isSuspended;
  // Read in callbacks, so a ref rather than a dependency — otherwise every page
  // the presenter turns would rebuild handleDocumentLoaded.
  const presenterDocIdRef = useRef<string | null>(null);
  presenterDocIdRef.current = resolvePresenterView(followModeState)?.documentId ?? null;
  /**
   * The user is deliberately looking at something other than the presenter's
   * document. Same flag, and the same reasoning, as SidePanel's — see the long
   * comment there for why this is local state and not Live Share suspension.
   *
   * The stage went without one until 2026-08-28, and that was the bug: opening
   * a document here while following put the presenter's straight back on top a
   * second later. It bit hardest on Teams mobile, where the stage IS the app —
   * a phone participant has no side panel to fall back to.
   */
  const [followOverride, setFollowOverride] = useState(false);
  /** Bumped by Rejoin so the apply effect re-runs on an unchanged document. */
  const [followEpoch, setFollowEpoch] = useState(0);
  /**
   * The user has taken over from the shared view in THIS mount, so the URL
   * path below must not reach in and replace what they chose. Belt to the
   * URL stamping's braces: the stamp makes a remount land in the right place,
   * this makes the live session immune even if a stale docId lingers.
   */
  const userOpenedRef = useRef(false);
  const mountCountRef = useRef<number>(0);
  if (mountCountRef.current === 0) mountCountRef.current = countMount();

  /**
   * The stage had no state readout, which is most of why the phone's follow
   * bug survived three rounds of remote guessing: the side panel has had one
   * since 2026-08-27, and the phone does not run the side panel. Same gesture
   * -- five taps on the wordmark within three seconds -- so it stays out of
   * certification screenshots.
   */
  const [showDebug, setShowDebug] = useState(false);
  // A delete control has to know when something is selected, and needs a way
  // to act on it. Until 2026-09-07 deleting was Delete/Backspace only, which a
  // phone has no way to press.
  const [hasSelection, setHasSelection] = useState(false);
  const deleteSelectedRef = useRef<(() => void) | null>(null);
  /**
   * Live page geometry for the readout. A ref, not state: the viewer owns the
   * Fabric canvas, and this must not re-render anything when it is populated.
   */
  const geometryReadoutRef = useRef<(() => Record<string, string>) | null>(null);
  // Same as SidePanel: the picker shows the selected text's size, so a tap on
  // another size always applies.
  const handleSelectionChange = useCallback((has: boolean, textSize: AnnotationTextSize | null) => {
    setHasSelection(has);
    if (textSize) setActiveTextSize(textSize);
  }, []);
  const tapsRef = useRef<number[]>([]);
  const handleBrandTap = useCallback(() => {
    const now = Date.now();
    tapsRef.current = [...tapsRef.current, now].filter((t) => now - t < 3000);
    if (tapsRef.current.length >= 5) {
      tapsRef.current = [];
      setShowDebug((v) => !v);
    }
  }, []);

  /**
   * The readout is computed during render, but the geometry it reports changes
   * when someone draws, zooms or turns a page -- none of which re-render this
   * component. Ticking only while the panel is open keeps it honest without
   * costing anything the rest of the time.
   */
  const [, setDebugTick] = useState(0);
  useEffect(() => {
    if (!showDebug) return;
    const id = window.setInterval(() => setDebugTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [showDebug]);

  const handleDocumentLoaded = useCallback(
    (doc: DocumentInfo) => {
      setDocumentInfo(doc);
      resetZoom();
      // Unconditional, and deliberately NOT gated on isFollowing. Opening a
      // document by hand is an explicit statement of intent; whether Live Share
      // reports "following" in this exact millisecond is a different question,
      // asked at the wrong moment. The overwrite lands LATER and asynchronously,
      // so a follow state that reads false now and true a second afterwards --
      // the Android file picker backgrounds the WebView, and Live Share already
      // alternates between the presenter's value and our own echo -- walked
      // straight past the old `if (isFollowingRef.current)` gate. Reported still
      // broken from a real phone on 2026-08-30 against exactly that gate.
      if (presenterDocIdRef.current) {
        appliedFollowDocIdRef.current = presenterDocIdRef.current;
      }
      setFollowOverride(true);
      userOpenedRef.current = true;
      // The one that actually survives the WebView being torn down.
      stampDocIdInUrl(doc.documentId);
    },
    [resetZoom],
  );

  const handleCloseDocument = useCallback(() => {
    setDocumentInfo(null);
    setShowExtraction(false);
    setActiveTool('select');
    // The whiteboard comes back in this document's place, and must not inherit
    // a zoom that was fitted to a different page.
    resetZoom();
    if (isFollowingRef.current) {
      if (presenterDocIdRef.current) {
        appliedFollowDocIdRef.current = presenterDocIdRef.current;
      }
      setFollowOverride(true);
    } else {
      appliedFollowDocIdRef.current = null;
    }
    userOpenedRef.current = true;
    // Otherwise a reload re-opens the document we just closed.
    clearDocIdInUrl();
  }, [resetZoom]);

  /** Desktop only: back to the upload prompt. What was drawn stays with the meeting. */
  const handleCloseWhiteboard = useCallback(() => {
    setWhiteboardClosed(true);
    setShowExtraction(false);
    setActiveTool('select');
  }, []);

  const handleOpenWhiteboard = useCallback(() => {
    setWhiteboardClosed(false);
  }, []);

  /** Back to the presenter's view. Clearing the applied id is what makes it act. */
  const handleRejoinPresenter = useCallback(() => {
    appliedFollowDocIdRef.current = null;
    setFollowOverride(false);
    setFollowEpoch((e) => e + 1);
    // Asking for the presenter's view back also means a reload should land
    // there, so hand the URL back to them.
    userOpenedRef.current = false;
    if (presenterDocIdRef.current) stampDocIdInUrl(presenterDocIdRef.current);
    else clearDocIdInUrl();
    if (isSuspendedRef.current) void endSuspension();
  }, [endSuspension]);

  // The stage deliberately does NOT broadcast view state, even while the local
  // user is the presenter.
  //
  // Sharing to the stage leaves the presenter running TWO instances of this
  // app in one meeting — this one and their side panel — and both saw
  // isPresenting. Both wrote follow state, so followers applied whichever
  // landed last and flickered between the two documents (observed on a phone
  // 2026-08-27, alternating between the previously shared PDF and a newly
  // uploaded image). Each instance also CONSUMES follow state, so they were
  // additionally following each other.
  //
  // The side panel is the control surface; the stage is a projection of what
  // was shared. It has no business steering everyone else's view, and it does
  // not need to: the meeting stage is already visible to every participant,
  // so navigating here is seen without any follow-me involvement.

  // Track page changes from PdfViewer

  // Follow presenter's document when following
  const { followDocument } = useFollowDocument(
    followModeState,
    isFollowing,
    documentInfo?.documentId,
  );
  // Guard on the id, not a one-shot ref like the URL path below: the presenter
  // may legitimately switch documents many times, but re-applying one we are
  // ALREADY showing would remount the viewer and throw away zoom and fit state
  // for no reason.
  const appliedFollowDocIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!followDocument) return;
    // The guard that actually holds — see followOverride above.
    if (followOverride) return;
    // Same as SidePanel: do not re-apply a document the presenter has closed.
    if (isWhiteboard(presenterDocIdRef.current)) return;
    if (appliedFollowDocIdRef.current === followDocument.documentId) return;
    appliedFollowDocIdRef.current = followDocument.documentId;
    // Applying their document IS rejoining them, however we got here.
    setFollowOverride(false);
    setDocumentInfo(followDocument);
    // followEpoch is a dependency so Rejoin can re-apply an unchanged document;
    // it is deliberately unused in the body.
  }, [followDocument, followEpoch, followOverride]);

  // The presenter closed their document and is on the whiteboard: follow them
  // there, as SidePanel does. Clearing the URL matters most here -- the stage
  // reloads its document from ?docId after a remount.
  const presenterViewDocId = resolvePresenterView(followModeState)?.documentId ?? null;
  const openDocumentId = documentInfo?.documentId;
  useEffect(() => {
    if (
      !shouldReturnToWhiteboard(presenterViewDocId, openDocumentId, isFollowing, followOverride)
    ) {
      return;
    }
    appliedFollowDocIdRef.current = WHITEBOARD_DOCUMENT_ID;
    setDocumentInfo(null);
    setShowExtraction(false);
    setActiveTool('select');
    resetZoom();
    clearDocIdInUrl();
  }, [presenterViewDocId, openDocumentId, isFollowing, followOverride, resetZoom]);

  // How the stage actually gets its document. Teams opens the stage as a fresh
  // instance of this app with empty state, and follow-me does not reach it —
  // verified in a real meeting 2026-08-21, presenter's stage AND follower's
  // stage both sat on the upload prompt. Share-to-stage puts the id in the URL
  // and we fetch it here.
  const { urlDocument } = useDocFromUrl(documentInfo?.documentId);
  const urlDocAppliedRef = useRef(false);
  useEffect(() => {
    // Never over the top of a document the user opened here themselves.
    if (userOpenedRef.current) return;
    if (urlDocument && !urlDocAppliedRef.current) {
      urlDocAppliedRef.current = true;
      // Record it as the follow document too. It IS the shared one, reached by
      // a different road, and leaving the ref null told the follow effect it
      // had never applied anything — so the moment the user opened a document
      // of their own here, the presenter's arrived and replaced it.
      appliedFollowDocIdRef.current = urlDocument.documentId;
      setDocumentInfo(urlDocument);
    }
  }, [urlDocument]);

  // Follow the presenter's page — never our own echoed back. See
  // resolveFollowPage for why isLocalValue has to be honoured.
  const followPage = resolveFollowPage(
    followModeState,
    isFollowing,
    isSuspended,
    documentInfo?.documentId,
  );

  // Keyed by document for the reason given in SidePanel: an unkeyed image
  // viewer kept the whiteboard's annotations over the next image opened.
  const renderViewer = (doc: DocumentInfo) => {
    if (doc.documentType === 'pdf' || doc.documentType === 'presentation') {
      return (
        <PdfViewer
          key={doc.documentId}
          pdfData={doc.data}
          documentId={doc.documentId}
          activeTool={activeTool}
          activeColor={activeColor}
          activeStrokeWidth={activeStrokeWidth}
          activeTextSize={activeTextSize}
          onToolChange={setActiveTool}
          followPage={followPage}
          isFollowing={isFollowing && !followOverride}
          onPageChange={setCurrentPage}
          zoomOverride={zoomLevel}
          onFitZoomCalculated={reportFitZoom}
          onSelectionChange={handleSelectionChange}
          deleteSelectedRef={deleteSelectedRef}
          geometryReadoutRef={geometryReadoutRef}
        />
      );
    }

    if (doc.documentType === 'document') {
      return (
        <DocxEditor
          key={doc.documentId}
          docxData={doc.data}
          fileName={doc.fileName}
          documentId={doc.documentId}
          zoomOverride={zoomLevel}
        />
      );
    }

    if (doc.documentType === 'spreadsheet') {
      return (
        <SpreadsheetEditor
          key={doc.documentId}
          spreadsheetData={doc.data}
          fileName={doc.fileName}
          documentId={doc.documentId}
          zoomOverride={zoomLevel}
        />
      );
    }

    return (
      <ImageViewer
        key={doc.documentId}
        imageData={doc.data}
        mimeType={doc.mimeType}
        documentId={doc.documentId}
        activeTool={activeTool}
        activeColor={activeColor}
        activeStrokeWidth={activeStrokeWidth}
        activeTextSize={activeTextSize}
        onToolChange={setActiveTool}
        zoomOverride={zoomLevel}
        onFitZoomCalculated={reportFitZoom}
        onExportReady={handleImageExportReady}
        onSelectionChange={handleSelectionChange}
        deleteSelectedRef={deleteSelectedRef}
        geometryReadoutRef={geometryReadoutRef}
      />
    );
  };

  return (
    <div className="meeting-stage">
      <div className="meeting-stage__header">
        <span className="meeting-stage__brand" onClick={handleBrandTap}>
          <BrandMark size={22} />
          <span className="meeting-stage__title">Annoteer</span>
        </span>
        {(documentInfo || (shownDocument && canCloseWhiteboard)) && (
          <button
            className="meeting-stage__close-doc"
            onClick={documentInfo ? handleCloseDocument : handleCloseWhiteboard}
            title={documentInfo ? 'Close document' : 'Close whiteboard'}
          >
            &times; Close
          </button>
        )}
        <UserAvatarStack users={viewers} maxVisible={5} />
        <ConnectionStatus state={connectionState} />
      </div>

      {(exportedName ?? imageExportedName) && !exportError && !imageExportError && (
        <div className="export-done" role="status">
          Saved <strong>{exportedName ?? imageExportedName}</strong>. If it did not appear, your
          Teams client may be blocking downloads — try Teams on Windows, macOS or the web.
        </div>
      )}

      {(exportError ?? imageExportError) && (
        <div className="export-error" role="alert">
          Download failed: {exportError ?? imageExportError}
        </div>
      )}

      <PickerInterruptedBanner />

      {showDebug && (
        <DebugPanel
          onClose={() => setShowDebug(false)}
          values={{
            build: BUILD_TAG,
            surface: 'stage',
            authed: authStatus,
            picker: FILE_INPUT_ACCEPT ? 'filtered' : 'any',
            mounts: mountCountRef.current,
            userOpened: userOpenedRef.current,
            lastUpload: lastUpload(),
            lastExport: lastExport(),
            renderScale: renderScale(),
            following: isFollowing,
            lsEvents: eventCount,
            lsType: followModeState?.type ?? null,
            suspended: isSuspended,
            override: followOverride,
            followType: followModeState?.type ?? null,
            isLocalValue: followModeState?.isLocalValue ?? null,
            presenterDoc: presenterDocIdRef.current?.slice(0, 8) ?? null,
            appliedDoc: appliedFollowDocIdRef.current?.slice(0, 8) ?? null,
            openDoc: documentInfo?.documentId.slice(0, 8) ?? null,
            openType: documentInfo?.documentType ?? null,
            urlDoc: new URLSearchParams(window.location.search).get('docId')?.slice(0, 8) ?? null,
            followPage: followPage ?? null,
            presenterPage: resolvePresenterView(followModeState)?.currentPage ?? null,
            page: currentPage,
            conn: formatConnectionRow(connectionState, shownDocument != null),
            // Page-relative geometry of every mark on the current page, read
            // from Fabric rather than Yjs. The two clients store identical
            // data in all three coordinate bugs found so far, so only the
            // rendered geometry can disagree -- and a fraction of the page is
            // scale-invariant, so the SAME numbers must appear on a phone and
            // on a desktop. Compare row g:<id> against g:<id>, and watch the
            // w/h as hard as the x/y: a shape can sit in exactly the right
            // place while being a third too small.
            ...(geometryReadoutRef.current?.() ?? {}),
          }}
        />
      )}

      <MeetingControls surface="stage" />

      {!shownDocument ? (
        <>
          <DocumentUpload onDocumentLoaded={handleDocumentLoaded} />
          <button className="side-panel__rejoin" onClick={handleOpenWhiteboard}>
            Open the whiteboard
          </button>
          {(followOverride || isSuspended) && (
            <button className="side-panel__rejoin" onClick={handleRejoinPresenter}>
              Rejoin the presenter
            </button>
          )}
        </>
      ) : (
        <div className="meeting-stage__content">
          {shownDocument.documentType !== 'document' &&
            shownDocument.documentType !== 'spreadsheet' && (
              <div className="meeting-stage__toolbar">
                <AnnotationToolbar
                  activeTool={activeTool}
                  activeColor={activeColor}
                  activeStrokeWidth={activeStrokeWidth}
                  onToolChange={setActiveTool}
                  onColorChange={setActiveColor}
                  onStrokeWidthChange={setActiveStrokeWidth}
                  activeTextSize={activeTextSize}
                  onTextSizeChange={setActiveTextSize}
                />
              </div>
            )}

          <div className="meeting-stage__main">
            <ZoomControls
              onCloseDocument={
                documentInfo
                  ? handleCloseDocument
                  : canCloseWhiteboard
                    ? handleCloseWhiteboard
                    : undefined
              }
              closeLabel={documentInfo ? undefined : 'Close whiteboard'}
              onRejoinPresenter={followOverride || isSuspended ? handleRejoinPresenter : undefined}
              openDocumentSlot={
                <DocumentUpload variant="inline" onDocumentLoaded={handleDocumentLoaded} />
              }
              zoom={zoomLevel}
              onZoomChange={chooseZoom}
              fitZoom={fitZoom}
              onFitSelect={chooseFit}
              fitMode={fitMode}
              canUndo={canUndo}
              canRedo={canRedo}
              onUndo={() => undoManager?.undo()}
              onRedo={() => undoManager?.redo()}
              canDelete={hasSelection}
              onDeleteSelected={() => deleteSelectedRef.current?.()}
              onExtractClick={
                shownDocument.documentType === 'pdf' ||
                shownDocument.documentType === 'presentation'
                  ? () => setShowExtraction(true)
                  : undefined
              }
              onExportClick={
                // Teams for Android refuses the download itself -- see the same
                // guard in SidePanel. Not our error, and not fixable from here.
                IS_COARSE_POINTER
                  ? undefined
                  : canExportAnnotated
                    ? () => void exportAnnotated()
                    : canExportImage
                      ? handleExportImage
                      : undefined
              }
              isExporting={isExporting || isExportingImage}
            />

            {!documentInfo && (
              <p className="whiteboard-hint" role="note">
                <strong>Whiteboard</strong> — everyone in the meeting can draw here.{' '}
                {IS_COARSE_POINTER
                  ? 'A shared document replaces it as soon as a presenter opens one.'
                  : 'Open a document to annotate it instead.'}
              </p>
            )}

            <div className="meeting-stage__viewer">
              <Suspense fallback={<div className="viewer-suspense">Loading viewer…</div>}>
                {renderViewer(shownDocument)}
              </Suspense>
            </div>

            {showExtraction &&
              (shownDocument.documentType === 'pdf' ||
                shownDocument.documentType === 'presentation') && (
                <ExtractionPanel
                  pdfData={shownDocument.data}
                  fileName={shownDocument.fileName}
                  onClose={() => setShowExtraction(false)}
                />
              )}
          </div>
        </div>
      )}
    </div>
  );
}
